<template>
    <div class="field-settings">
        <div class="field-settings__grid">
            <!-- Name -->
            <Field
                v-model="localField.name"
                :field="{
                    name: lang('fields::fields.field-editor.field_name'),
                    slug: 'name',
                    type: 'text',
                    adminComponent: 'AdminText',
                }"
                label-position="top"
                @change="emitUpdate"
            />

            <!-- Slug -->
            <Field
                v-model="localField.slug"
                :field="{
                    name: lang('fields::fields.field-editor.field_slug'),
                    slug: 'slug',
                    type: 'slug',
                    adminComponent: 'AdminSlug',
                }"
                :source="isNewField ? localField.name : ''"
                :slugify-from-source="isNewField"
                :forbidden="siblingFieldSlugs"
                :params="{ separator: '_' }"
                label-position="top"
                @change="emitUpdate"
            />

            <!-- Type -->
            <Field
                v-model="localField.type"
                :field="{
                    name: lang('fields::fields.field-editor.type'),
                    slug: 'type',
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    options: types,
                }"
                :searchable="true"
                label-position="top"
                @change="emitUpdate"
            />

        </div>

        <div class="field-settings__checkboxes">
            <!-- Required -->
            <Field
                v-model="localField.required"
                :field="{
                    name: lang('fields::fields.field-editor.is_required'),
                    slug: 'required',
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
                label-position="left"
                @change="emitUpdate"
            />

            <!-- Multiple (for file, select) -->
            <Field
                v-if="showMultiple"
                v-model="localField.multiple"
                :field="{
                    name: lang('fields::fields.field-editor.is_multiple'),
                    slug: 'multiple',
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
                label-position="left"
                @change="emitUpdate"
            />

            <!-- Has Tooltip -->
            <Field
                v-model="localField.has_tooltip"
                :field="{
                    name: lang('fields::fields.field-editor.has_tooltip'),
                    slug: 'has_tooltip',
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
                label-position="left"
                @change="emitUpdate"
            />
        </div>

        <!-- Tooltip text -->
        <div v-if="localField.has_tooltip" class="field-settings__conditional">
            <Field
                v-model="localField.tooltip"
                :field="{
                    name: lang('fields::fields.field-editor.tooltip'),
                    slug: 'tooltip',
                    type: 'textarea',
                    adminComponent: 'AdminTextarea',
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Mask (for number type) -->
        <div v-if="localField.type === 'number'" class="field-settings__conditional">
            <Field
                v-model="localField.mask"
                :field="{
                    name: lang('fields::fields.field-editor.mask'),
                    slug: 'mask',
                    type: 'select',
                    adminComponent: 'AdminSelect',
                    options: maskOptions,
                }"
                label-position="top"
                @change="emitUpdate"
            />
        </div>

        <!-- Options (for select type) -->
        <div v-if="localField.type === 'select'" class="field-settings__conditional">
            <label class="field-settings__section-label">
                {{ lang('fields::fields.field-editor.options') }}
            </label>
            <Field
                v-model="localField.options"
                :field="{
                    name: '',
                    slug: 'options',
                    type: 'repeater',
                    adminComponent: 'AdminRepeater',
                    sortable: true,
                    fields: {
                        id: {
                            name: lang('fields::fields.field-editor.option'),
                            slug: 'id',
                            type: 'text',
                            adminComponent: 'AdminText',
                        },
                    },
                }"
                label-position="none"
                @change="emitUpdate"
            />
        </div>

        <!-- Type-specific settings (non-field-editor) -->
        <template v-if="Object.keys(regularTypeAdminSettings).length > 0">
            <div class="field-settings__type-settings">
                <h4 class="field-settings__type-settings-title">
                    {{ lang('fields::fields.field-editor.type_settings') }}
                </h4>
                <template v-for="(setting, key) in regularTypeAdminSettings" :key="key">
                    <Field
                        :model-value="localField[key]"
                        :field="setting"
                        label-position="left"
                        @update:model-value="updateFieldProperty(key as string, $event)"
                    />
                </template>
            </div>
        </template>

        <!-- Sub-field editors (Group, Repeater, Tab) -->
        <template v-for="(setting, key) in fieldEditorSettings" :key="key">
            <div class="field-settings__subfields">
                <h4 class="field-settings__subfields-title">
                    {{ setting.name }}
                </h4>
                <FieldEditor
                    :model-value="localField[key] ?? []"
                    :field="{ name: '', slug: String(key), type: 'field-editor', group: 'fields' }"
                    @update:model-value="updateFieldProperty(String(key), $event)"
                />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineAsyncComponent } from 'vue';
import { lang } from '@/lang/composable';
import { defaultMaskNames } from '@src/Common/Composables/fieldMask';

const FieldEditor = defineAsyncComponent(() => import('./FieldEditor.vue'));

interface FieldData {
    slug: string;
    name: string;
    type: string;
    required?: boolean;
    multiple?: boolean;
    labelPosition?: string;
    has_tooltip?: boolean;
    tooltip?: string;
    mask?: string;
    options?: any[];
    [key: string]: any;
}

interface Props {
    field: FieldData;
    types: { id: string; name: string; admin?: Record<string, any> }[];
    siblingFieldSlugs?: string[];
}

const props = withDefaults(defineProps<Props>(), {
    siblingFieldSlugs: () => [],
});

// A field is "new" if it had no slug when first loaded (not yet saved)
const isNewField = ref(!props.field.slug);

const emit = defineEmits<{
    (event: 'update:field', field: FieldData): void;
}>();

const localField = ref<FieldData>({ ...props.field });

watch(
    () => props.field,
    (newField) => {
        localField.value = { ...newField };
    },
    { deep: true },
);

const showMultiple = computed(() => ['file', 'select'].includes(localField.value.type));

const typeAdminSettings = computed(() => {
    const currentType = props.types.find((t) => t.id === localField.value.type);
    return currentType?.admin ?? {};
});

const fieldEditorSettings = computed(() => {
    return Object.fromEntries(
        Object.entries(typeAdminSettings.value).filter(([, setting]) => setting.type === 'field-editor'),
    );
});

const regularTypeAdminSettings = computed(() => {
    return Object.fromEntries(
        Object.entries(typeAdminSettings.value).filter(([, setting]) => setting.type !== 'field-editor'),
    );
});

const maskOptions = computed(() => [
    { id: null, name: lang('fields::fields.field-editor.mask_default') },
    ...defaultMaskNames,
]);

const emitUpdate = () => {
    emit('update:field', { ...localField.value });
};

const updateFieldProperty = (key: string, value: any) => {
    localField.value[key] = value;
    emitUpdate();
};
</script>
